import { useState } from 'react';
import { useAtomValue } from 'jotai';
import { Box } from '@material-ui/core';
import Popover from '@material-ui/core/Popover';
import { makeStyles } from '@material-ui/core/styles';
import { MDSTextField, MDSTheme } from '@marqvision/mds/core';
import { MDSButton, MDSTypography, MDSPlainButton, MDSIcon } from '@marqvision/mds-v2';
import { userProfileAtom } from '~/atoms';

const useStyles = makeStyles(() => ({
  popoverPaper: {
    borderRadius: '8px',
    boxShadow: '0px 0px 2px rgba(0, 0, 0, 0.16), 0px 8px 16px rgba(0, 0, 0, 0.2)',
    marginTop: '4px',
    width: '364px',
  },
  numberWrapper: {
    ...MDSTheme.typography.medium,
    color: MDSTheme.palette.bluegray[900],
    display: 'flex',
    padding: '16px',
    gap: '10px',
    alignItems: 'end',
  },
}));

type Props = {
  format?: 'currency' | 'number';
  anchorEl: HTMLElement;
  min: number | null;
  max: number | null;
  onClose: () => void;
  onApply: (min: number | null, max: number | null) => void;
};

export const NumberRangePicker = (props: Props): JSX.Element => {
  const classes = useStyles();
  const { format, anchorEl, onClose, onApply } = props;

  const [minNumber, setMinNumber] = useState<string | undefined>(
    props.min && props.min !== 0 ? String(props.min) : undefined
  );
  const [maxNumber, setMaxNumber] = useState<string | undefined>(
    props.max && props.max !== 0 ? String(props.max) : undefined
  );

  const [isReset, setIsReset] = useState<boolean>(false);

  const userProfile = useAtomValue(userProfileAtom);

  const validation =
    isReset || Number(minNumber) < Number(maxNumber) || (minNumber && !maxNumber) || (!minNumber && maxNumber);

  const clear = () => {
    setMinNumber('');
    setMaxNumber('');
  };

  const handleApply = () => {
    const parsedMinNumber = isNaN(Number(minNumber)) ? null : Number(minNumber);
    const parsedMaxNumber = isNaN(Number(maxNumber)) ? null : Number(maxNumber);
    onApply(parsedMinNumber, parsedMaxNumber);
    onClose();
  };

  return (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      classes={{
        paper: classes.popoverPaper,
      }}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
      TransitionProps={{
        onExited: clear,
      }}
    >
      <Box className={classes.numberWrapper}>
        <Box>
          <Box display="flex" style={{ marginBottom: '6px' }}>
            <MDSTypography
              variant="body"
              size="m"
              weight="medium"
              color="color/content/neutral/default/normal"
              style={{ marginRight: '2px' }}
            >
              Min
            </MDSTypography>
            <MDSTypography variant="body" size="s" weight="regular" color="color/content/neutral/secondary/normal">
              (Optional)
            </MDSTypography>
          </Box>
          <MDSTextField
            type="number"
            inputProps={{ step: '0.01' }}
            size="medium"
            placeholder="Enter"
            value={minNumber}
            onChange={(e: unknown, value: unknown) => {
              setMinNumber(value as string);
              setIsReset(false);
            }}
          />
        </Box>
        to
        <Box>
          <Box display="flex" style={{ marginBottom: '6px' }}>
            <MDSTypography
              variant="body"
              size="m"
              weight="medium"
              color="color/content/neutral/default/normal"
              style={{ marginRight: '2px' }}
            >
              Max
            </MDSTypography>
            <MDSTypography variant="body" size="s" weight="regular" color="color/content/neutral/secondary/normal">
              (Optional)
            </MDSTypography>
          </Box>
          <MDSTextField
            type="number"
            inputProps={{ step: '0.01' }}
            size="medium"
            placeholder="Enter"
            value={maxNumber}
            onChange={(e: unknown, value: unknown) => {
              setMaxNumber(value as string);
              setIsReset(false);
            }}
          />
        </Box>
        {format === 'currency' && userProfile?.client_currency}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        padding="8px 8px 8px 16px"
        borderTop={`1px solid ${MDSTheme.palette.bluegray[100]}`}
      >
        <MDSPlainButton
          size="small"
          color="blue"
          startIcon={<MDSIcon.Reset />}
          isDisabled={!minNumber && !maxNumber}
          onClick={() => {
            clear();
            setIsReset(true);
          }}
        >
          Reset
        </MDSPlainButton>
        <Box display="flex" gridGap="10px">
          <MDSPlainButton size="small" color="bluegray" onClick={onClose}>
            Cancel
          </MDSPlainButton>
          <MDSButton variant="fill" size="medium" color="blue" isDisabled={!validation} onClick={handleApply}>
            Apply
          </MDSButton>
        </Box>
      </Box>
    </Popover>
  );
};
